import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import FormDialog from './formdialog';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Link } from "react-router-dom";
import authService from '../api-authorization/AuthorizeService';
import { HelpText } from "../HelpText";
import SimpleBackdrop from "../backdrop";
import Box from "@mui/material/Box";
import RandomQuote from "../randomQuote";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Cancel';
import moment from "moment";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export default function Channels({ updateTitle }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        const response = await fetch('/api/channels');
        authService.followIfLoginRedirect(response);
        const data = await response.json();
        setItems(data);
        setLoading(false);
    };

    const refresh = () => {
        fetchData().catch(console.error);
    };

    const openDeleteDialog = (id) => {
        setSelectedId(id);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setSelectedId(null);
        setDeleteDialogOpen(false);
    };

    const confirmDelete = async () => {
        if (!selectedId) return;
        try {
            const response = await fetch(`/api/channels/${selectedId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                setItems((prev) => prev.filter(item => item.channel.id !== selectedId));
            } else {
                alert("Failed to delete the subscription.");
            }
        } catch (error) {
            console.error("Delete error:", error);
            alert("An error occurred while deleting.");
        } finally {
            closeDeleteDialog();
        }
    };

    useEffect(() => {
        updateTitle('Channels');
        fetchData().catch(console.error);
    }, []);

    const noEntries = () => (
        <TableRow>
            <TableCell colSpan={12} align="center">
                You haven't yet added any channels.
                <Box mt={5}>
                    <RandomQuote />
                </Box>
            </TableCell>
        </TableRow>
    );

    return (
        <div>
            <FormDialog items={items} cb={refresh} />

            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2, boxShadow: 3, borderRadius: 2 }}>
                <TableContainer>
                    <Table stickyHeader sx={{ minWidth: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Channel</TableCell>
                            <TableCell>Reach</TableCell>
                            <TableCell>Activity</TableCell>
                            <TableCell>Last Posted</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Affiliate</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row) => (
                                <TableRow hover key={row.id}>
                                    {/* Channel name + handle */}
                                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {/* Internal link to channel details */}
                                        <div style={{ fontWeight: 600 }}>
                                            <Link
                                                to={`/channels/${row.channel.id}`}
                                                style={{ textDecoration: 'none'}}
                                            >
                                                {row.channel.channel_name}
                                            </Link>
                                        </div>

                                        {/* Outbound link to YouTube with external icon */}
                                        <a
                                            href={row.channel.channel_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: "0.85rem",
                                                color: "#555",
                                                textDecoration: "none",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}
                                        >
                                            @{row.channel.handle}
                                            <OpenInNewIcon fontSize="inherit" style={{ opacity: 0.6 }} />
                                        </a>
                                    </TableCell>

                                    {/* Reach */}
                                    <TableCell>
                                        <div>{row.channel.subscriber_count?.toLocaleString()}</div>
                                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                                            {row.channel.view_count?.toLocaleString()} views
                                        </div>
                                    </TableCell>

                                    {/* Activity */}
                                    <TableCell>
                                        <div>{row.channel.total_discovered_videos} vids</div>
                                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                                            ~{row.channel.average_days_between_video?.toFixed(1)} days
                                        </div>
                                    </TableCell>

                                    {/* Last posted */}
                                    <TableCell>
                                        {row.channel.last_posted
                                            ? moment(row.channel.last_posted).format("MMM DD, YYYY")
                                            : "—"}
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell>
                                        {row.channel.verified && <CheckIcon color="success" fontSize="small" />}
                                    </TableCell>
                                    <TableCell>
                                        {row.channel.is_affiliate && <MonetizationOnIcon color="success" fontSize="small" />}
                                    </TableCell>

                                    {/* Source */}
                                    <TableCell>{row.channel.channel_type}</TableCell>

                                    {/* Actions */}
                                    <TableCell align="right">
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => openDeleteDialog(row.channel.id)}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        {(!items || items.length === 0) && noEntries()}
                    </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={items.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(+e.target.value);
                        setPage(0);
                    }}
                />
            </Paper>

            <HelpText>
                These are specific channels you are subscribed to. Videos from these channels will be analysed to see
                if they qualify for being reported into a project.
                <br /><br />
                Discovered videos is the amount of videos we have in that source.
            </HelpText>

            <SimpleBackdrop open={loading} />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this subscription? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
